import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, ShoppingBag, Eye, X } from 'lucide-react';
import { useCart, useWishlist } from '../context/AppContext';

export default function ProductCard({ product }) {
  const navigate = useNavigate();
  const { addToCart } = useCart();
const { toggleWishlist, isWished } = useWishlist();

const wished = isWished(product._id);
  const [imgError, setImgError] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [activeImg, setActiveImg] = useState(0);

  const images = product.images?.length
    ? product.images
    : [{ url: '/images/jewelry-placeholder.jpg' }];

  const disc =
    product.discountPrice && product.price > product.discountPrice
      ? Math.round(((product.price - product.discountPrice) / product.price) * 100)
      : 0;

  const mainImage =
    !imgError && images[0]?.url ? images[0].url : '/images/jewelry-placeholder.jpg';

  return (
    <>
      <style>{`
        .product-card {
          background: #fff;
          border: 1px solid rgba(201,169,110,0.24);
          border-radius: 10px;
          overflow: hidden;
          cursor: pointer;
          transition: all 0.35s ease;
          position: relative;
          min-height: 380px;
          display: flex;
          flex-direction: column;
        }

        .product-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 22px 48px rgba(107,27,42,0.15);
          border-color: rgba(201,169,110,0.65);
        }

        .prod-img-wrap {
          width: 100%;
          height: 230px;
          position: relative;
          overflow: hidden;
          background: linear-gradient(135deg,#F7EFE4,#E8D4BD);
        }

        .prod-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.7s ease;
        }

        .product-card:hover .prod-img {
          transform: scale(1.09);
        }

        .prod-img-wrap::after {
          content: "";
          position: absolute;
          top: 0;
          left: -130%;
          width: 70%;
          height: 100%;
          background: linear-gradient(110deg, transparent, rgba(255,255,255,0.38), transparent);
          transform: skewX(-18deg);
          transition: left 0.8s ease;
          pointer-events: none;
        }

        .product-card:hover .prod-img-wrap::after {
          left: 135%;
        }

        .prod-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(180deg, transparent 30%, rgba(50,10,20,0.82));
          display: flex;
          align-items: flex-end;
          justify-content: center;
          gap: 10px;
          padding-bottom: 22px;
          opacity: 0;
          transition: opacity 0.32s ease;
        }

        .product-card:hover .prod-overlay {
          opacity: 1;
        }

        .prod-overlay-btn {
          background: rgba(255,255,255,0.96);
          color: var(--maroon);
          border: none;
          padding: 10px 16px;
          font-size: 11px;
          letter-spacing: 1.4px;
          text-transform: uppercase;
          cursor: pointer;
          border-radius: 30px;
          font-family: 'Jost', sans-serif;
          transition: all 0.25s ease;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .prod-overlay-btn:hover {
          background: #C9A96E;
          color: white;
        }

        .prod-tag {
          position: absolute;
          top: 12px;
          left: 12px;
          z-index: 3;
        }

        .prod-wish {
          position: absolute;
          top: 12px;
          right: 12px;
          background: rgba(255,255,255,0.96);
          border: 1px solid rgba(201,169,110,0.28);
          width: 38px;
          height: 38px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: 0 4px 14px rgba(0,0,0,0.12);
          transition: all 0.25s ease;
          z-index: 3;
          color: var(--maroon);
        }

        .prod-wish:hover,
        .prod-wish.active {
          background: var(--maroon);
          color: white;
        }

        .prod-info {
          padding: 17px 17px 20px;
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .prod-cat {
          font-size: 10px;
          letter-spacing: 2px;
          text-transform: uppercase;
          color: #A07850;
          margin-bottom: 5px;
        }

        .prod-name {
          font-family: 'Cormorant Garamond', serif;
          font-size: 20px;
          font-weight: 500;
          color: var(--text-dark);
          margin-bottom: 12px;
          line-height: 1.25;
          min-height: 50px;
        }

        .prod-pricing {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
          margin-top: auto;
        }

        .prod-price {
          font-size: 16px;
          font-weight: 600;
          color: var(--maroon);
        }

        .prod-orig {
          font-size: 13px;
          color: var(--text-light);
          text-decoration: line-through;
        }

        .prod-disc {
          font-size: 10px;
          background: #FFF0F0;
          color: #C62828;
          padding: 2px 6px;
          border-radius: 2px;
        }

        .quick-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(25,8,12,0.72);
          z-index: 2000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 22px;
        }

        .quick-modal {
          width: min(940px, 100%);
          background: var(--ivory);
          border-radius: 14px;
          overflow: hidden;
          display: grid;
          grid-template-columns: 1fr 1fr;
          position: relative;
          box-shadow: 0 30px 90px rgba(0,0,0,0.32);
          animation: quickFade .25s ease;
        }

        @keyframes quickFade {
          from { opacity: 0; transform: translateY(20px) scale(.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }

        .quick-close {
          position: absolute;
          top: 14px;
          right: 14px;
          width: 38px;
          height: 38px;
          border-radius: 50%;
          border: none;
          background: white;
          color: var(--maroon);
          z-index: 3;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .quick-image-main {
          height: 520px;
          background: var(--cream);
        }

        .quick-image-main img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .quick-content {
          padding: 44px 34px;
        }

        .quick-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 38px;
          line-height: 1.1;
          font-weight: 300;
          color: var(--text-dark);
          margin-bottom: 12px;
        }

        .quick-desc {
          font-size: 14px;
          color: var(--text-mid);
          line-height: 1.8;
          margin: 18px 0 24px;
        }

        .quick-thumbs {
          display: flex;
          gap: 8px;
          margin-top: 12px;
        }

        .quick-thumb {
          width: 58px;
          height: 58px;
          border-radius: 8px;
          overflow: hidden;
          border: 2px solid var(--border);
          cursor: pointer;
        }

        .quick-thumb.active {
          border-color: var(--maroon);
        }

        .quick-thumb img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        @media(max-width:600px) {
  .product-card {
    min-height: 280px;
    border-radius: 8px;
  }

  .prod-img-wrap {
    height: 165px;
  }

  .prod-info {
    padding: 10px 10px 12px;
  }

  .prod-cat {
    font-size: 8px;
    letter-spacing: 1.2px;
    margin-bottom: 4px;
  }

  .prod-name {
    font-size: 15px;
    min-height: 38px;
    margin-bottom: 8px;
    line-height: 1.2;
  }

  .prod-price {
    font-size: 14px;
  }

  .prod-orig {
    font-size: 11px;
  }

  .prod-disc {
    font-size: 8px;
  }

  .prod-wish {
    width: 30px;
    height: 30px;
    top: 8px;
    right: 8px;
  }

  .prod-overlay {
    display: none;
  }
}
          .quick-modal {
            grid-template-columns: 1fr;
            max-height: 92vh;
            overflow-y: auto;
          }

          .quick-image-main {
            height: 340px;
          }
        }
      `}</style>

      <div
        className="product-card"
        onClick={() => navigate(`/product/${product.slug || product._id}`)}
      >
        {product.tags?.includes('new') && (
          <span className="prod-tag tag badge-new">New</span>
        )}

        {product.tags?.includes('bestseller') && (
          <span className="prod-tag tag badge-bestseller">Best Seller</span>
        )}

        <button
  className={`prod-wish${wished ? ' active' : ''}`}
  onClick={(e) => {
    e.stopPropagation();
    toggleWishlist(product);
  }}
>
  <Heart
    size={15}
    fill={wished ? "white" : "none"}
  />
</button>

        <div className="prod-img-wrap">
          <img
            src={mainImage}
            alt={product.name}
            className="prod-img"
            onError={() => setImgError(true)}
          />

          <div className="prod-overlay">
            <button
              className="prod-overlay-btn"
              onClick={(e) => {
                e.stopPropagation();
                setQuickOpen(true);
                setActiveImg(0);
              }}
            >
              <Eye size={13} />
              Quick View
            </button>

            <button
              className="prod-overlay-btn"
              onClick={(e) => {
                e.stopPropagation();
                addToCart(product);
              }}
            >
              <ShoppingBag size={13} />
              Add
            </button>
          </div>
        </div>

        <div className="prod-info">
          <div className="prod-cat">{product.category}</div>
          <div className="prod-name">{product.name}</div>

          <div className="prod-pricing">
            <span className="prod-price">
              ₹{(product.discountPrice || product.price).toLocaleString()}
            </span>

            {disc > 0 && (
              <>
                <span className="prod-orig">₹{product.price.toLocaleString()}</span>
                <span className="prod-disc">{disc}% off</span>
              </>
            )}
          </div>
        </div>
      </div>

      {quickOpen && (
        <div
          className="quick-backdrop"
          onClick={() => setQuickOpen(false)}
        >
          <div
            className="quick-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="quick-close" onClick={() => setQuickOpen(false)}>
              <X size={18} />
            </button>

            <div>
              <div className="quick-image-main">
                <img
                  src={images[activeImg]?.url || '/images/jewelry-placeholder.jpg'}
                  alt={product.name}
                />
              </div>

              {images.length > 1 && (
                <div className="quick-thumbs" style={{ padding: '12px' }}>
                  {images.slice(0, 3).map((img, i) => (
                    <div
                      key={i}
                      className={`quick-thumb${activeImg === i ? ' active' : ''}`}
                      onClick={() => setActiveImg(i)}
                    >
                      <img src={img.url} alt="" />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="quick-content">
              <span className="tag badge-bestseller">{product.category}</span>

              <h2 className="quick-title">{product.name}</h2>

              <div className="prod-pricing">
                <span className="prod-price">
                  ₹{(product.discountPrice || product.price).toLocaleString()}
                </span>

                {disc > 0 && (
                  <>
                    <span className="prod-orig">₹{product.price.toLocaleString()}</span>
                    <span className="prod-disc">{disc}% off</span>
                  </>
                )}
              </div>

              <p className="quick-desc">
                {product.description || 'Premium stainless steel jewellery crafted for everyday elegance.'}
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <button
                  className="btn btn-primary btn-full btn-lg"
                  onClick={() => addToCart(product)}
                >
                  <ShoppingBag size={16} />
                  Add to Cart
                </button>

                <button
                  className="btn btn-outline btn-full btn-lg"
                  onClick={() => navigate(`/product/${product.slug || product._id}`)}
                >
                  View Full Details
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}